import { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faBolt,
  faCalendarPlus,
  faCheck,
  faChevronDown,
  faChevronUp,
  faCreditCard,
  faDoorOpen,
  faFileSignature,
  faHouse,
  faLink,
  faListCheck,
  faPhone,
  faUser,
  faWrench
} from '@fortawesome/free-solid-svg-icons';
import { Avatar, Composer, MessageBubble, StatusPill, SuggestionCard } from '../../components';
import { formatPhone } from '../../utils/callLog';
import { ROLE_META } from './constants';
import TemplatePicker from './TemplatePicker';
import HandOffDrawer from '../Todos/HandOffDrawer';
import { Btn } from '../../components/az';
import {
  useActions,
  useActionStep,
  useContactActions,
  useConversationMode,
  useSetConversationMode
} from '../../queries/actions';
import { useAssistantName } from '../../queries/agents';
import { useMeQuery } from '../../queries/me';
import { usePhone } from '../../hooks/usePhone';
import { useConversationThread } from './useConversationThread';
import {
  ActionBanner,
  ActionBannerBtns,
  ActionBannerHead,
  ActionBannerSub,
  ActionStrip,
  BackBtn,
  GhostBtn,
  HeadActions,
  History,
  HistoryNote,
  Intent,
  MaggieOn,
  QuickRow,
  StripToggle,
  Thread,
  ThreadFoot,
  ThreadHead,
  ThreadName,
  ThreadNameText,
  ThreadRoute
} from './styled';

const firstName = (name) => String(name || '').split(' ')[0] || 'there';

// Type-based quick actions mirroring the design's m-quick row.
// draft: fills the composer (reviewed before send); schedule: opens the scheduling flow.
const QUICK = {
  prospect: [
    { label: 'Schedule tour', icon: faCalendarPlus, schedule: true },
    { label: 'Send application link', icon: faFileSignature, draft: (n) => `Hi ${n}, here's your application link: ` },
    { label: 'Send listing link', icon: faLink, draft: (n) => `Hi ${n}, here's the listing: ` }
  ],
  tenant: [
    { label: 'Schedule maintenance', icon: faWrench, schedule: true },
    { label: 'Send payment link', icon: faCreditCard, draft: (n) => `Hi ${n}, here's your secure payment link: ` },
    { label: 'Send portal link', icon: faDoorOpen, draft: (n) => `Hi ${n}, here's your tenant portal link: ` }
  ],
  owner: [
    { label: 'Schedule a call', icon: faCalendarPlus, schedule: true },
    { label: 'Send portal link', icon: faDoorOpen, draft: (n) => `Hi ${n}, here's your owner portal link: ` }
  ],
  vendor: [{ label: 'Schedule a job', icon: faWrench, schedule: true }]
};
QUICK.applicant = QUICK.prospect;

const primaryLabel = (x) => (x.approve?.label || '').replace(/^Approve: /, '') || 'Confirm';

export default function ThreadPanel({ onBack, conversation, canSend, draft, onDraftChange, onProfile, onTemplatePick, logOnly }) {
  const { contactNumber } = conversation;
  const t = useConversationThread(contactNumber, canSend);
  const historyRef = useRef(null);
  const role = ROLE_META[conversation.type] || ROLE_META.contact;
  const navigate = useNavigate();
  const agent = useAssistantName();
  const { data: me } = useMeQuery();
  const { tz } = useActions('d');
  const open = useContactActions(contactNumber);
  const needs = open.filter((x) => x.lane === 'needs' || x.lane === 'review');
  const { data: mode } = useConversationMode(contactNumber);
  const setMode = useSetConversationMode(contactNumber);
  const step = useActionStep();
  const [handing, setHanding] = useState(false);
  const [stripOpen, setStripOpen] = useState(false);
  const phone = usePhone();
  const setDetail = useOutletContext()?.setDetail;
  const title = conversation.name || formatPhone(contactNumber);
  const inShell = phone && Boolean(onBack && setDetail);
  const backRef = useRef(onBack);
  backRef.current = onBack;

  useEffect(() => {
    if (!inShell) return undefined;
    setDetail({ title, onBack: () => backRef.current() });
    return () => setDetail(null);
  }, [inShell, title, setDetail]);

  useEffect(() => {
    const el = historyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [t.messages.length, contactNumber]);

  const sendDraft = () => {
    const body = draft.trim();
    onDraftChange('');
    t.send(body, { onError: () => onDraftChange(body) });
  };

  const editSuggestion = () => {
    onDraftChange(t.suggestion.suggestion);
    t.dismissSuggestion();
  };

  const confirmAction = (x) =>
    step.mutate({ id: x._id, step: 'approve' }, { onSuccess: () => toast(`Confirmed: ${x.title}.`) });

  const runQuick = (q) => {
    if (q.schedule) return navigate('/scheduling', { state: { contactNumber } });
    onDraftChange(q.draft(firstName(conversation.name)));
  };

  const quickActions = QUICK[conversation.type] || [];
  const handOff = canSend ? (
    <Btn sm icon={faBolt} onClick={() => setHanding(true)}>
      Hand off to {agent}
    </Btn>
  ) : null;

  return (
    <Thread aria-label={`Conversation with ${conversation.name || formatPhone(contactNumber)}`}>
      {!logOnly && (
      <>
      <ThreadHead>
        {onBack && !inShell && (
          <BackBtn type="button" aria-label="Back to conversations" onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </BackBtn>
        )}
        <Avatar name={conversation.name || formatPhone(contactNumber)} size={40} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <ThreadName>
            <ThreadNameText>{conversation.name || formatPhone(contactNumber)}</ThreadNameText>
            {conversation.type !== 'contact' && <StatusPill color={role.color}>{role.label}</StatusPill>}
            {mode?.mode === 'ai' && (
              <MaggieOn>
                <FontAwesomeIcon icon={faBolt} />
                {agent} on
              </MaggieOn>
            )}
          </ThreadName>
          <ThreadRoute>
            {conversation.property && (
              <span>
                <FontAwesomeIcon icon={faHouse} />
                {conversation.property}
              </span>
            )}
            <span>
              <FontAwesomeIcon icon={faPhone} />
              {formatPhone(contactNumber)}
            </span>
          </ThreadRoute>
        </div>
        <HeadActions>
          {conversation.needsAttention && canSend && (
            <GhostBtn type="button" aria-label="Resolve" onClick={t.resolve} disabled={t.resolving}>
              <FontAwesomeIcon icon={faCheck} />
              <span>Resolve</span>
            </GhostBtn>
          )}
          <GhostBtn as="a" href={`tel:${contactNumber}`} aria-label="Call">
            <FontAwesomeIcon icon={faPhone} />
            <span>Call</span>
          </GhostBtn>
          <GhostBtn type="button" aria-label="Profile" onClick={onProfile}>
            <FontAwesomeIcon icon={faUser} />
            <span>Profile</span>
          </GhostBtn>
          {needs.length > 0 && (
            <Btn sm icon={faListCheck} onClick={() => navigate('/todos', { state: { openActionId: needs[0]._id } })}>
              {needs.length} in Agent Actions
            </Btn>
          )}
        </HeadActions>
      </ThreadHead>

      {mode?.mode === 'ai' && (
        <Intent $ai>
          <FontAwesomeIcon icon={faBolt} />
          <b style={{ flex: 1, fontSize: 13 }}>{agent} is handling this conversation</b>
          {canSend && (
            <Btn sm kind="primary" onClick={() => setMode.mutate('human')}>
              Take over
            </Btn>
          )}
        </Intent>
      )}
      {mode?.mode === 'human' && (
        <Intent>
          <FontAwesomeIcon icon={faUser} />
          <b style={{ flex: 1, fontSize: 13 }}>You're handling this. {agent} won't reply.</b>
          {canSend && (
            <Btn sm onClick={() => setMode.mutate('ai')}>
              Hand back to {agent}
            </Btn>
          )}
        </Intent>
      )}

      {needs.length > 0 && (
        <ActionStrip>
          {phone && (
            <StripToggle type="button" aria-expanded={stripOpen} onClick={() => setStripOpen(!stripOpen)}>
              <FontAwesomeIcon icon={faListCheck} />
              <span>{`${needs.length} ${needs.length === 1 ? 'action' : 'actions'} in Agent Actions`}</span>
              <FontAwesomeIcon icon={stripOpen ? faChevronUp : faChevronDown} />
            </StripToggle>
          )}
          {(!phone || stripOpen) && needs.map((x) => (
            <ActionBanner key={x._id}>
              <ActionBannerHead>
                <FontAwesomeIcon icon={faBolt} />
                {x.title}
              </ActionBannerHead>
              {x.suggest && <ActionBannerSub>{agent} suggests: {x.suggest}</ActionBannerSub>}
              <ActionBannerBtns>
                {canSend && (
                  <Btn sm kind="primary" onClick={() => confirmAction(x)} disabled={step.isPending}>
                    {primaryLabel(x)}
                  </Btn>
                )}
                <Btn sm onClick={() => navigate('/todos', { state: { openActionId: x._id } })}>
                  Details
                </Btn>
              </ActionBannerBtns>
            </ActionBanner>
          ))}
        </ActionStrip>
      )}
      </>
      )}

      <History ref={historyRef} aria-live="polite" aria-busy={t.loading}>
        {t.loading ? (
          <HistoryNote>Loading messages…</HistoryNote>
        ) : t.messages.length === 0 ? (
          <HistoryNote>No messages in this conversation yet.</HistoryNote>
        ) : (
          t.messages.map((m) => <MessageBubble key={m._id} message={m} />)
        )}
      </History>

      <ThreadFoot>
        {(t.suggestion || t.suggestionLoading) && (
          <SuggestionCard
            suggestion={t.suggestion?.suggestion}
            basis={t.suggestion?.basis}
            loading={t.suggestionLoading}
            sending={t.sending}
            onUse={() => t.send(t.suggestion.suggestion)}
            onEdit={editSuggestion}
            onDismiss={t.dismissSuggestion}
          />
        )}
        <QuickRow>
          <TemplatePicker onPick={onTemplatePick} disabled={!canSend} />
          {quickActions.map((q) => (
            <Btn key={q.label} sm icon={q.icon} disabled={!canSend} onClick={() => runQuick(q)}>
              {q.label}
            </Btn>
          ))}
          {phone && handOff}
        </QuickRow>
        <Composer
          value={draft}
          onChange={onDraftChange}
          onSend={sendDraft}
          sending={t.sending}
          disabled={!canSend}
          extra={phone ? null : handOff}
        />
      </ThreadFoot>
      {handing && (
        <HandOffDrawer
          contact={{ name: conversation.name || '', phone: contactNumber }}
          agent={agent}
          orgName={me?.organization?.name || ''}
          tz={tz}
          openNeed={needs[0]}
          showingAt={needs.find((x) => x.kind === 'showing')?.dueAt}
          onClose={() => setHanding(false)}
        />
      )}
    </Thread>
  );
}
